import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import cx from 'classnames'
import { Tabs } from '@manychat/manyui'

import { useAppDispatch } from 'reduxTyped'
import { useGoalEditorContext } from 'apps/aiAutomation/components/EngagementGoalEditor/lib/context'
import {
  GoalEditorMode,
  GoalTabs,
  SubmitRef,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/types'
import {
  formTabsConfig,
  isActiveLeadQualGoal,
} from 'apps/aiAutomation/components/EngagementGoalEditor/lib/utils'
import { AiAutomationLayoutActions } from 'apps/aiAutomation/store/layout'
import { EngagementGoal, GoalStatus, GoalType } from 'shared/api/requests/aiEngagementGoal/schemas'
import { useMediaQuery } from 'utils/commonHooks'

import { LeadQualPlayground } from '../LeadQualPlayground'
import { LeadQualStats } from '../LeadQualStats'

import { Editor } from './components/Editor'

import cm from './styles.module.css'

export const LeadQualEditor = ({
  passedRef,
}: {
  passedRef: RefObject<SubmitRef<EngagementGoal> | null>
}) => {
  const dispatch = useAppDispatch()

  const isMobile = useMediaQuery('(max-width: 1024px)')

  const { mode, goal, changeMode } = useGoalEditorContext(GoalType.LEAD_QUAL)

  const [isPlaygroundOpened, setIsPlaygroundOpened] = useState(mode !== GoalEditorMode.CREATE)

  const [currentTab, setCurrentTab] = useState<GoalTabs>(
    isActiveLeadQualGoal(goal) ? GoalTabs.STATS : GoalTabs.SETUP,
  )

  const tabsConfig = useMemo(() => formTabsConfig(goal, isMobile), [goal, isMobile])

  const showTabs = mode !== GoalEditorMode.CREATE

  const onTabChange = useCallback(
    (tab: GoalTabs) => {
      setCurrentTab(tab)

      switch (tab) {
        case GoalTabs.STATS:
        case GoalTabs.PREVIEW:
          changeMode(
            goal.status === GoalStatus.STOPPED
              ? GoalEditorMode.SAVING_BLOCKED
              : GoalEditorMode.READ,
          )
          break
        case GoalTabs.SETUP:
          changeMode(goal.status === GoalStatus.STOPPED ? GoalEditorMode.EDIT : GoalEditorMode.READ)
          break
      }
    },
    [changeMode, goal?.status],
  )

  const onGoalCreate = useCallback(() => {
    dispatch(AiAutomationLayoutActions.expandSidebarChanged(true))

    setIsPlaygroundOpened(true)
  }, [dispatch])

  useEffect(() => {
    if (mode === GoalEditorMode.EDIT) {
      setCurrentTab(GoalTabs.SETUP)
    }
  }, [mode])

  return (
    <div className={cm.editor}>
      <div className={cx(cm.columns, isPlaygroundOpened && !isMobile && cm.columns_expanded)}>
        <div className={cx(cm.contentColumn, showTabs && cm.contentColumn_withTabs)}>
          {showTabs && (
            <Tabs
              className={cm.tabsContainer}
              value={currentTab}
              onChange={onTabChange}
              options={tabsConfig}
            />
          )}

          {currentTab === GoalTabs.STATS && <LeadQualStats />}

          {currentTab === GoalTabs.SETUP && <Editor ref={passedRef} onCreate={onGoalCreate} />}
        </div>

        {((isPlaygroundOpened && !isMobile) || currentTab === GoalTabs.PREVIEW) && (
          <LeadQualPlayground />
        )}
      </div>
    </div>
  )
}
